import { Component, OnInit, ChangeDetectorRef, OnDestroy, HostListener } from '@angular/core';
import { firstValueFrom, from, Observable, of, Subscription, tap } from 'rxjs';
import { filter, first, map, pairwise } from 'rxjs/operators';
import { Router, RoutesRecognized, ActivatedRoute } from '@angular/router';
import { ServerDataService } from '@core/services/server-data/server-data.service';
import { Location } from '@angular/common';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { ShareService } from '@core/services/share-data/share-data.service';
import { EngineService } from '@core/services/engine/engine.service';
import { SetupPageService } from '@core/services/setup-page/setup-page.service';
import { TranslateService } from '@ngx-translate/core';
import {
	STORAGE_NAMES,
	SELECTORS,
	VIEW_TYPES,
	Countries,
	COOKIE_LINK,
	GOOGLE_ANALYTICS_SCRIPTS,
	CLASSES,
	IV_VIEW_TYPES,
} from '@root/app.config';
import { DisabledLinkModalComponent } from '@shared/modals/disabled-link-modal/disabled-link-modal.component';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ModalService } from '@core/services/modal/modal.service';
import { BackgroundConfig, BlindSetup, BlindTypes, IVSceneSettings, IVViewType, Material, ServerMaterial } from '@root/app.interfaces';
import { IndexedDBService } from '@core/services/indexed-db/indexed-db.service';
import { BackgroundService } from '@core/services/background-service/background.service';
import { LinkUnavailableModalComponent } from '@shared/modals/link-unavailable-modal/link-unavailable-modal.component';
import { ActionTrackingService } from '@core/services/action-tracking/action-tracking.service';
import { AnotherDeviceModalComponent } from '@shared/modals/another-device-modal/another-device-modal.component';
import { DetectionService } from '@core/services/detection/detection.service';

import * as _ from 'lodash';
import { GrowthBookService } from '@core/services/growthbook/growthbook.service';
import { GtmService } from '@core/services/gtm/gtm.service2';
import { VariantService } from '@core/services/variant/variant.service';
import { BlindStateService } from '@core/services/blind-state/blind-state.service';
import { PassDataService } from '@core/services/pass-data/pass-data.service';
import { PartAvailabilityService } from '@core/services/part-availability/part-availability.service';
import { LoaderService } from '@core/services/loader/loader.service';
import { PaymentSessionServiceService } from '@core/services/payment-session-service/payment-session-service.service';
import { PaymentNavigationServiceService } from '@core/services/payment-navigation-service/payment-navigation-service.service';
import { ResetAppService } from '@core/services/reset-app/reset-app.service';
import { GoogleMapsLoaderService } from '@core/services/google-maps-loader/google-maps-loader.service';
import { EmbeddedLinkReportingService } from '@core/services/embedded-link-reporting/embedded-link-reporting.service';
import { ViewModeService } from '@core/services/view-mode/view-mode.service';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit, OnDestroy {
	title = '3dc';
	sessionKey = '';
	getCountry: Subscription;
	getRouteEvents: Subscription;
	getServerData: Subscription;
	getDefaultServerData: Subscription;
	getSessionLink: Subscription;
	getSessionData: Subscription;
	getRetailerRequest: Subscription;
	getActionBackPage: Subscription;
	getActivatedRouteRules: Subscription;
	getModelLoaded: Subscription;
	getResetApp: Subscription;
	userVisitTime = 0;
	sceneCreated = false;
	feedbackTooltipOpened = false;
	countryLink = '';
	timerId: ReturnType<typeof setTimeout>;
	currentLink: string;
	isGrowthBookReady = false;

	constructor(
		protected router: Router,
		private serverData: ServerDataService,
		private location: Location,
		private matDialog: MatDialog,
		private modalService: ModalService,
		private route: ActivatedRoute,
		private setupPageService: SetupPageService,
		private shareService: ShareService,
		private resetAppService: ResetAppService,
		private indexedDBService: IndexedDBService,
		private changeDetection: ChangeDetectorRef,
		private engineService: EngineService,
		private backgroundService: BackgroundService,
		private readonly translateService: TranslateService,
		private sessionStorageService: SessionStorageService,
		private actionTrackingService: ActionTrackingService,
		private growthbookService: GrowthBookService,
		private detectionService: DetectionService,
		private gtmService: GtmService,
		private variant: VariantService,
		private blindStateService: BlindStateService,
		private passDataService: PassDataService,
		private partsAvailabilityService: PartAvailabilityService,
		private loaderService: LoaderService,
		private paymentSessionService: PaymentSessionServiceService,
		private paymentNavigationService: PaymentNavigationServiceService,
		private googleMapsLoader: GoogleMapsLoaderService,
		private embeddedLinkReportingService: EmbeddedLinkReportingService,
		private viewModeService: ViewModeService,
	) {
		this.paymentNavigationService.init();
	}

	@HostListener('window:online', ['$event'])
	@HostListener('window:offline', ['$event'])
	networkStatus(event: Event) {
		this.shareService.setNetworkStatus({
			event,
			status: event.type === 'online',
		});
	}

	setDefaultLang() {
		this.translateService.addLangs(['en']);
		this.translateService.setDefaultLang('en');

		const browserLanguage: any = this.translateService.getBrowserLang();
		this.translateService.use(browserLanguage.match(/en/) ? browserLanguage : 'en');
	}

	ngOnInit(): void {
		void this.googleMapsLoader.load();
		this.cookieHandler();
		this.googleAnalyticsHandler();
		// Log user access
		this.detectionService.access();
		this.setDefaultLang();
		this.resetAppService.resetAllSessionStorageData();
		this.resetCountryHandler();
		this.getRetailerId().then(() => this.bindEvent());

		this.growthbookService.isReady$.subscribe((ready) => {
			this.isGrowthBookReady = ready;
		});

		this.getModelLoaded = this.shareService.getSceneCreated.subscribe(() => {
			if ((this.countryLink || this.sessionKey) && !this.sceneCreated) {
				this.getDefaultServerDataHandler();
			}

			this.sceneCreated = true;

			const isDesignType = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_view_type) === VIEW_TYPES.design;
			const sizeDisclaimer = this.sessionStorageService.getSession(STORAGE_NAMES.zip_size_disclaimer);

			if (isDesignType) {
				this.engineService.setBackgroundImage(null);

				if (sizeDisclaimer) {
					this.sessionStorageService.removeBlindData(STORAGE_NAMES.zip_size_disclaimer);
				}
			}

			this.openFeedbackTooltip();
			this.changeDetection.markForCheck();
		});

		this.getCountry = this.shareService.getCountry.subscribe((res: string) => {
			this.countryLink = res;

			this.changeDetection.markForCheck();
		});
	}

	ngOnDestroy(): void {
		this.getRouteEvents.unsubscribe();
		this.getServerData.unsubscribe();
		this.getDefaultServerData.unsubscribe();
		this.getSessionLink.unsubscribe();
		this.getSessionData.unsubscribe();
		this.getRetailerRequest.unsubscribe();
		this.getActivatedRouteRules.unsubscribe();
		this.getModelLoaded.unsubscribe();
		this.getCountry.unsubscribe();
		this.getResetApp.unsubscribe();
	}

	bindEvent(): void {
		const getProductType = this.setupPageService.getProductType();
		const currentDomainLink = this.location.path().split('?');

		this.currentLink = currentDomainLink[0].split('/')[1] || 'home';
		const splitQuery = currentDomainLink[1]?.split('=');
		const sessionKeyQuery = splitQuery && splitQuery[0] !== 'country' ? currentDomainLink[1]?.split('=')[1] : '';
		this.sessionKey = this.parseQueryParams() ? sessionKeyQuery.split('&')[0] : sessionKeyQuery;

		// Placed here because it only needs to be called once.
		if (this.sessionKey && this.currentLink === 'home') {
			this.sessionStorageService.setSession(true, STORAGE_NAMES.zip_image_visualisation_status);
		}

		// A `?view=` deep link (kept across reloads via session storage) only wins on a fresh /home visit -
		// a saved session (session key) always takes precedence, so drop the stored mode once one is present.
		if (this.currentLink === 'home') {
			if (this.sessionKey) {
				this.viewModeService.clearPersistedMode();
			} else {
				this.viewModeService.appliedMode = this.viewModeService.requestedMode;
			}
		}

		this.getActivatedRouteRules = this.route.data.subscribe(() => {
			this.routeCurrentPageHandler('');
			this.checkActivePageHandler();

			if (!this.sessionKey && this.countryLink && getProductType === 'all') {
				setTimeout(this.setDefaultLoader.bind(this, true));
			}

			this.changeDetection.markForCheck();
		});

		this.getSessionLink = this.shareService.getLinkSessionKey.subscribe((res) => {
			this.routeCurrentPageHandler(res);

			this.changeDetection.markForCheck();
		});

		this.getUserVisitTime();
		this.getActionBackPage = this.router.events
			.pipe(
				filter((evt: any) => evt instanceof RoutesRecognized),
				pairwise(),
			)
			.subscribe((events: RoutesRecognized[]) => {
				const prevUrl = events[0].urlAfterRedirects.split('?')[0];
				const curUrl = events[1].urlAfterRedirects.split('?')[0];

				if ((prevUrl === '/overview' || prevUrl === '/quote') && curUrl === '/home') {
					this.sceneCreated = false;
					setTimeout(() => this.shareService.setActionBackPage(true));
				}
			});
	}

	parseQueryParams(): boolean {
		const urlParams = new URLSearchParams(window.location.search);
		const countryCode = urlParams.get('country') || this.sessionStorageService.getSession(STORAGE_NAMES.zip_country_code);

		const hasCountryCode = this.setupPageService.validateCountryCodeHandler(countryCode?.toLowerCase());

		this.setupPageService.sendCountryCodeForRender(countryCode, hasCountryCode);

		return !!countryCode;
	}

	routeCurrentPageHandler(session: string): void {
		this.sessionKey = session ? session : this.sessionKey;

		const sessionKeyParam = {
			'': this.sessionKey,
			country: this.countryLink,
		};
		const queryParam = this.sessionKey ? { queryParams: sessionKeyParam } : {};
		void this.router.navigate([this.location.path().split('?')[0]], { ...queryParam, replaceUrl: true });
	}

	setDefaultLoader(isLoading: boolean): void {
		this.loaderService.setLoading(isLoading);
	}

	checkActivePageHandler(): void {
		if (this.currentLink !== 'home') {
			if (!this.sessionKey) {
				this.setEmptyBlindData(true);
			} else if (this.countryLink) {
				this.getDefaultServerDataHandler();
			}

			this.resetAppService.resetBlindSessionStorageData();
		}
	}

	getDefaultServerDataHandler(): void {
		this.getDefaultServerData = (
			this.passDataService.sessionConfig
				? of(this.passDataService.sessionConfig)
				: this.serverData.GetServerData(this.countryLink, this.sessionKey)
		).subscribe(
			async (res) => {
				if (!_.isEmpty(res.blinds)) {
					this.gtmService.sessionReturn(res.session_key, 1); // TODO: need to calculate and/or save return number to server // Missing app version param
				} else {
					this.gtmService.startSession(res.session_key, !!res.retailer); // Missing app version param
				}

				// This solution should only be kept if we don't otherwise modify the product offering for the shop variant
				// If the product offering for the shop variant changes, we should modify the backend to only send through the relevant products instead
				if (this.variant.isShop()) {
					delete res.interior;
				}

				this.setupMaterialsFromServer(res.outdoor.material);
				if (res.interior) {
					this.setupMaterialsFromServer(res.interior.material);
				}

				this.passDataService.sessionConfig = null;
				this.sessionStorageService.setBlindData(res.session_key, STORAGE_NAMES.zip_blind_temp_session);
				this.sessionStorageService.setBlindData(res, STORAGE_NAMES.zip_blind_config);
				this.sessionStorageService.setBlindData(res.text, STORAGE_NAMES.zip_blind_text);

				if (res.retailer) {
					this.sessionStorageService.setBlindData(res.retailer, STORAGE_NAMES.zip_embedded_retailer);
					this.sessionStorageService.setBlindData(res.embed_key, STORAGE_NAMES.zip_embed_key);
					this.shareService.setEmbeddedRetailer(true);
					this.embeddedLinkReportingService.armFirstEmbeddedInteractionTracking();
				}

				await this.setIVSettingsFromServer(res.iv_scene_settings, res.iv_screenshot);

				if (!_.isEmpty(res.blinds)) {
					this.setBlindDataFromServer(res);
				} else {
					this.setEmptyBlindData();
				}
			},
			(error) => {
				setTimeout(this.setDefaultLoader.bind(this, false));

				if (this.paymentSessionService.isPaid()) {
					this.paymentSessionService.openOrderPaidModal();
					return;
				}

				const errorModalComponent = this.matDialog.getDialogById('error-modal');

				if (errorModalComponent) {
					errorModalComponent.close();
					errorModalComponent.afterClosed().subscribe(() => this.openLinkUnavailableModalComponent(error));
				} else {
					this.openLinkUnavailableModalComponent(error);
				}
			},
		);
	}

	setupMaterialsFromServer(materials: ServerMaterial[]) {
		materials.forEach((material) => {
			material.color ??= {};

			if (material.color.can_be_changed == null) {
				material.color.can_be_changed = (material.available_colors?.length ?? 0) > 1;
			}

			if (material.color.default == null) {
				material.color.default = material.available_colors?.find((color) => color.is_default)?.color ?? '#ffffff';
			}
		});
	}

	openLinkUnavailableModalComponent(error: string): void {
		this.modalService.openResponsiveDialog({
			component: LinkUnavailableModalComponent,
			data: error.split('\n')[0].replace(/^\D+/g, ''),
			...this.modalService.getConfig(LinkUnavailableModalComponent.ID),
		});
	}

	async setIVSettingsFromServer(ivSceneSettings: IVSceneSettings, ivScreenshot: string) {
		if (ivScreenshot) {
			this.sessionStorageService.setBlindData(ivScreenshot, STORAGE_NAMES.zip_iv_screenshot);
		}

		const ivCurrentBackground = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_image_visualisation_background);

		if (!ivSceneSettings || ivSceneSettings.iv_last_type === IV_VIEW_TYPES.no_background) {
			this.sessionStorageService.setSession(false, STORAGE_NAMES.zip_image_visualisation_status);
		}

		if (!ivSceneSettings || ivCurrentBackground) {
			return;
		}

		const ivSceneUpload = ivSceneSettings.upload;
		const ivSceneSample = ivSceneSettings.sample;
		const ivLastType = ivSceneSettings.iv_last_type as IVViewType;

		this.setOpeningDataFromServer(ivSceneSettings);

		if (ivSceneUpload) {
			this.sessionStorageService.setBlindData(ivSceneUpload, STORAGE_NAMES.zip_image_visualisation_background);
			this.sessionStorageService.setBlindData(ivSceneUpload, STORAGE_NAMES.zip_uploaded_image_visualisation_background);

			this.backgroundService.cropperSettings = ivSceneUpload.cropper_settings;
			await firstValueFrom(this.saveBackgroundImageFromServer(ivSceneUpload));
		}

		if (ivSceneSample) {
			this.sessionStorageService.setBlindData(ivSceneSample, STORAGE_NAMES.zip_image_visualisation_background);

			const { type, index, image_orientation } = ivSceneSample;
			await this.backgroundService.getIVSampleImageFromSettings(type, index, image_orientation).then((image) => {
				image = { ...ivSceneSample, ...image };
				this.indexedDBService.saveImage(image, STORAGE_NAMES.zip_image_visualisation_background);
			});
		}

		this.sessionStorageService.setBlindData(ivLastType, STORAGE_NAMES.zip_iv_type);

		if (ivLastType === IV_VIEW_TYPES.live_background) {
			this.sessionStorageService.removeBlindData(STORAGE_NAMES.zip_image_visualisation_background);
		}

		if (ivLastType === IV_VIEW_TYPES.project_sample || ivLastType === IV_VIEW_TYPES.upload) {
			this.sessionStorageService.setSession(true, STORAGE_NAMES.zip_image_visualisation_status);
		}
	}

	private saveBackgroundImageFromServer(backgroundConfig: BackgroundConfig): Observable<void> {
		if (!backgroundConfig?.image) {
			return of(void 0);
		}

		return from(this.backgroundService.getIVImageFromSettings(backgroundConfig.image)).pipe(
			tap((image) => {
				const mergedImage = { ...backgroundConfig, ...image };
				this.indexedDBService.saveImage(mergedImage, STORAGE_NAMES.zip_image_visualisation_background);
			}),
			map(() => void 0),
		);
	}

	setBlindDataFromServer(res): void {
		let blindType = this.sessionStorageService.getSession(STORAGE_NAMES.zip_blind_type);
		const sessionBlinds = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);

		if (this.sessionKey && _.isEmpty(sessionBlinds)) {
			res.blinds.forEach((blind) => {
				blind.setup = this.setupConfigured(blind.setup, blind.type);
			});
			this.sessionStorageService.setBlindData(res.blinds, STORAGE_NAMES.zip_blind_data);
			this.blindStateService.reconcileFromStorage();
			this.partsAvailabilityService.checkBlindsValidityFromStorage();
		}

		this.sessionStorageService.setBlindData(true, STORAGE_NAMES.zip_blind_session_saved);
		this.sessionStorageService.setBlindData(res.session_key, STORAGE_NAMES.zip_blind_session);

		if (!blindType) {
			blindType = res.blinds[res.blinds.length - 1].type;
			this.sessionStorageService.setSession(blindType, STORAGE_NAMES.zip_blind_type);
		}

		this.shareService.setSessionBlindsData(true);

		this.shareService.setLoadingSpinner(false);
		const iVStatus = this.sessionStorageService.getSession(STORAGE_NAMES.zip_image_visualisation_status);
		const anotherDeviceDialog = this.matDialog.getDialogById(AnotherDeviceModalComponent.ID);
		this.setDefaultLoader(!!iVStatus && !anotherDeviceDialog);
		setTimeout(this.engineService.displayModelHandler.bind(this.engineService));

		this.changeDetection.markForCheck();
		return;
	}

	setupConfigured(setup: BlindSetup, type: string): BlindSetup {
		if (!setup) {
			return setup;
		}

		const defaultConfigs = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_config)[type];

		if (!setup.material) {
			const defaultMaterial = defaultConfigs.material.find((material) => material.is_default);
			const colorArr = defaultMaterial.available_colors.find((color) => color.is_default);

			setup.material = {
				id: defaultMaterial.id,
				name: defaultMaterial.name,
				color: defaultMaterial.color.default,
				color_name: colorArr.name,
				opacity: defaultMaterial.opacity,
				type: defaultMaterial.type,
				configured: false,
			} as Material;
		} else {
			setup.material.configured = true;
		}

		if (!setup.mounting) {
			const defaultMounting = defaultConfigs.frame.mounting.find((mounting) => mounting.is_default);
			setup.mounting = defaultMounting;
			setup.mounting.configured = false;
		} else {
			setup.mounting.configured = true;
		}

		if (!setup.operation) {
			const defaultOperation = defaultConfigs.frame.operation.find((operation) => operation.is_default);
			setup.operation = defaultOperation;
			setup.operation.optional = [];
			setup.operation.configured = false;
		} else {
			setup.operation.configured = true;
		}

		if (!setup.size || setup.size.width === 0 || setup.size.height === 0) {
			const defaultSize = defaultConfigs.material.find((material) => material.is_default)?.sizes;
			setup.size = {
				width: defaultSize.width.default,
				height: defaultSize.height.default,
				extra_size: false,
				configured: false,
			};
		} else {
			setup.size.configured = true;
		}

		if (!setup.frames.bottom_bar && type !== BlindTypes.Interior) {
			const defaultBottomBar = defaultConfigs.frame.bottom_bar_protection.find((bottomBar) => bottomBar.is_default);
			setup.frames.bottom_bar = {
				id: defaultBottomBar.id,
				name: defaultBottomBar.name,
				configured: false,
			};
		} else if (setup.frames.bottom_bar) {
			setup.frames.bottom_bar.configured = true;
		}

		if (setup.frames.frame_color.id === 0 || !setup.frames.frame_color.id) {
			const defaultFrameColor = defaultConfigs.frame.frame_color.find((frameColor) => frameColor.is_default);
			setup.frames.frame_color = {
				id: defaultFrameColor.id,
				name: defaultFrameColor.name,
				color: defaultFrameColor.color,
				configured: false,
			};
		} else {
			setup.frames.frame_color.configured = true;
		}

		if (!setup.frames.top_style && Object.hasOwn(setup.frames, 'top_style')) {
			const defaultTopStyle = defaultConfigs.frame.top_style.find((topStyle) => topStyle.is_default);
			setup.frames.top_style = {
				id: defaultTopStyle.id,
				name: defaultTopStyle.name,
				configured: false,
			};
		} else if (setup.frames.top_style) {
			setup.frames.top_style.configured = true;
		}

		return setup;
	}

	setOpeningDataFromServer(res): void {
		const sessionOpenings = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_openings_data);

		if (this.sessionKey && _.isEmpty(sessionOpenings) && res?.openings) {
			res.openings.forEach((opening) => (opening.isFitted = false));
			this.sessionStorageService.setBlindData(res.openings, STORAGE_NAMES.zip_openings_data);
			if (res.detectionResult?.imageSize) {
				// For backwards compatibility
				this.sessionStorageService.setSession(res.detectionResult.imageSize, STORAGE_NAMES.zip_detection_image_size);
			} else {
				this.sessionStorageService.setSession(res.detectionImageSize, STORAGE_NAMES.zip_detection_image_size);
			}
			this.shareService.setDetectionDone(true); // Predicated on the fact that a session key can only be created after detection is done. // Theoretically possible for session key to be created first but unlikely
		}
	}

	setEmptyBlindData(status?: boolean): void {
		const getStorageData = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);

		if (!getStorageData && this.currentLink !== 'home') {
			void this.router.navigate(['/home']);
		}

		this.shareService.setLoadingSpinner(false);
		setTimeout(this.setDefaultLoader.bind(this, false));

		if (status) {
			this.parseQueryParams();
		} else if (!getStorageData) {
			if (this.viewModeService.appliedMode === VIEW_TYPES.design) {
				this.startDesignOnlySetup();
			} else {
				this.setupPageService.setWelcomeModal();
			}
		}

		this.changeDetection.markForCheck();
		return;
	}

	/**
	 * `?view=dyb` deep link: skip the "choose a background" welcome modal and go
	 * straight into design mode, creating the first blind the same way the rest of
	 * the app does (blind type modal when both product types are available,
	 * otherwise a direct scene creation).
	 */
	startDesignOnlySetup(): void {
		this.sessionStorageService.setSession(IV_VIEW_TYPES.no_background, STORAGE_NAMES.zip_iv_type);
		this.sessionStorageService.setBlindData(VIEW_TYPES.design, STORAGE_NAMES.zip_view_type);

		const productType = this.setupPageService.getProductType();

		if (productType === 'all') {
			this.setupPageService.sendBlindTypeForRender(true);
			return;
		}

		const createFirstBlind = () => {
			const id = this.setupPageService.generateUniqueBlindId();

			this.engineService.createScene(productType, id.toString());
			this.sessionStorageService.setSession(productType, STORAGE_NAMES.zip_blind_type);
			this.shareService.setBlindType({ type: productType, id, buttonPressed: true });
		};

		// Wait until the base scene the engine builds (on load, or while rebuilding after a
		// country switch) is fully ready. Creating the blind while that scene is still loading
		// corrupts mesh names and crashes the shutter / bounding-box handlers. `getSceneCreated`
		// is a hot stream, so also handle the case where it has already fired.
		if (this.engineService.isModelCreated && this.engineService.getRootMeshes()?.length) {
			createFirstBlind();
		} else {
			this.shareService.getSceneCreated.pipe(first()).subscribe(createFirstBlind);
		}
	}

	getUserVisitTime(): void {
		this.userVisitTimer();

		this.getRetailerRequest = this.shareService.getRetailerRequest.subscribe(() => {
			clearInterval(this.timerId);

			this.changeDetection.markForCheck();
		});
	}

	getRetailerId(): Promise<boolean> {
		this.resetAppService.resetEmbeddedLinkSessionStorageData();

		return new Promise((resolve) => {
			const urlParams = new URLSearchParams(window.location.search);
			const retailerKey = urlParams.get('embed') || this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_embed_key);
			const embeddedRetailer = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_embedded_retailer);

			if (urlParams.get('embed')) {
				window.history.pushState(null, null, window.location.pathname);
			}

			if (retailerKey && !embeddedRetailer) {
				setTimeout(this.loaderService.setLoading.bind(this.loaderService, true, CLASSES.full_opacity));
				this.sessionStorageService.setBlindData(retailerKey, STORAGE_NAMES.zip_embed_key);

				this.serverData.GetEmbeddedLink(retailerKey).subscribe(
					(res) => {
						if (_.isEmpty(res)) {
							this.matDialog.open(DisabledLinkModalComponent, this.modalService.getConfig(DisabledLinkModalComponent.ID));
							this.loaderService.setLoading(false);
							return;
						}

						this.sessionStorageService.setBlindData(res, STORAGE_NAMES.zip_embedded_retailer);

						const countryCode =
							Countries.find((country) => country.name === res.country)?.code || Countries.find((country) => country.is_default)?.code;
						this.sessionStorageService.setSession(countryCode, STORAGE_NAMES.zip_country_code);
						this.countryLink = countryCode;
						this.shareService.setEmbeddedStatus(true);
						this.embeddedLinkReportingService.armFirstEmbeddedInteractionTracking();

						this.loaderService.setLoading(false);
						resolve(true);
					},
					() => {
						this.loaderService.setLoading(false);
					},
				);
			} else {
				resolve(false);
			}
		});
	}

	userVisitTimer(): void {
		this.shareService.setUserTime(this.userVisitTime);

		this.timerId = setTimeout(() => {
			this.userVisitTime += 1;
			this.userVisitTimer();
		}, 1000);
	}

	cookieHandler(): void {
		const cookiesTag = document.querySelector(SELECTORS.cookies_tag) as HTMLScriptElement;
		cookiesTag.src = COOKIE_LINK;
	}

	googleAnalyticsHandler() {
		const head = document.getElementsByTagName('head')[0];

		GOOGLE_ANALYTICS_SCRIPTS.forEach((scriptAttributes: any) => {
			const scriptElement = document.createElement('script');

			Object.entries(scriptAttributes).forEach(([key, value]) => {
				scriptElement[key] = value;
			});

			head.appendChild(scriptElement);
		});
	}

	resetCountryHandler(): void {
		this.getResetApp = this.resetAppService.onReset$.subscribe(({ country }) => {
			if (country) {
				this.countryLink = country.code; // technically redundant as countryLink should have already been updated by getCountry subscription in ngOnInit, however, I've left it here just in case there is some werid race condition which causes countryLink to not have been updated before this subscription is triggered.
			}

			this.sessionKey = '';
			this.getDefaultServerDataHandler();
		});
	}

	openFeedbackTooltip(): void {
		const FEEDBACK_DELAY = 60000;

		const getFeedbackStatus = this.sessionStorageService.getSession(STORAGE_NAMES.zip_feedback_status);
		const getBlindData = this.sessionStorageService.getSession(STORAGE_NAMES.zip_blind_data);
		const shouldOpenTooltip = !this.feedbackTooltipOpened && !getFeedbackStatus && getBlindData;

		if (shouldOpenTooltip) {
			this.feedbackTooltipOpened = true;
			setTimeout(() => this.shareService.setFeedbackTooltipStatus(true), FEEDBACK_DELAY);
		}
	}
}
